import type { InferOutput } from "valibot";
import { boolean, object } from "valibot";
import { initIntegrationDefinition } from "../Integration";
import type { IncompleteIntegrationDefinition } from "../types";
import { useTranslation } from "../../state/i18n";
import ServiceAccountSelectInput from "../../components/inputs/ServiceAccountSelectInput";
import { MAX_CHARACTERS_SM, MAX_CHARACTERS_XL } from "../../constants";
import { Checkbox } from "../../components/checkbox/Checkbox";
import { serviceAccount } from "../../utils/validation/base";
import ServiceAccountService from "../../services/ServiceAccountService";
import MixedInput from "../../components/inputs/MixedInput";
import { createMemo } from "solid-js";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { useFormServices } from "../../services/formServices";
import type { FieldType } from "../../types";
import { parseStringTemplate, stringTemplateToString } from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { isNil } from "lodash-es";
import ServiceAccountId from "../../state/ServiceAccountId";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

const ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

const ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];

type ConfigurationDto = {
  serviceAccount?: string;
  path: string;
  filename: string;
  csv: boolean;
};

const Schema = object({
  serviceAccount: serviceAccount(),
  path: stringTemplate(
    ALLOWED_PLACEHOLDER_FIELDS,
    ALLOWED_PLACEHOLDER_VARIABLES,
    ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_XL,
  ),
  filename: stringTemplate(
    ALLOWED_PLACEHOLDER_FIELDS,
    ALLOWED_PLACEHOLDER_VARIABLES,
    ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_SM,
  ),
  csv: boolean(),
});

export type OneDriveIntegrationConfiguration = InferOutput<typeof Schema>;
const OneDriveIntegration: IncompleteIntegrationDefinition<OneDriveIntegrationConfiguration, ConfigurationDto> = {
  type: "onedrive",
  legacy: {
    namespace: "com.moreapps.plugins",
    key: "onedrive",
    version: 7,
  },
  schema: Schema,
  icon: integrationsIcons["onedrive"],
  titleKey: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: () => {
    const { t } = useTranslation();

    return {
      serviceAccount: undefined,
      path: [],
      filename: parseStringTemplate(
        t(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_FILENAME_DEFAULT_VALUE),
        [],
        [],
      ),
      csv: false,
    };
  },
  propertyKeys: {
    serviceAccount: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_LABEL),
    path: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_PATH_LABEL),
    filename: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_FILENAME_LABEL),
    csv: keyFromSelector(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_CSV_LABEL),
  },
  fromDto: (configuration, fields, variables) => ({
    serviceAccount: isNil(configuration.serviceAccount)
      ? undefined
      : ServiceAccountId.from(configuration.serviceAccount),
    path: parseStringTemplate(configuration.path, fields, variables),
    filename: parseStringTemplate(configuration.filename, fields, variables),
    csv: configuration.csv,
  }),
  toDto: (configuration, fields, variables) => ({
    serviceAccount: configuration.serviceAccount?.toString(),
    path: stringTemplateToString(configuration.path, fields, variables),
    filename: stringTemplateToString(configuration.filename, fields, variables),
    csv: configuration.csv,
  }),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    const placeholderMenuOptions = createMemo(() =>
      createDynamicValueOptions(t, {
        fields: fieldService
          .getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS)
          .flatMap((field) => toFieldValueOptions(field)),
        subforms: [],
        customVariables: variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES).map(toVariableValueOption),
        metaVariables: ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
      }),
    );

    return (
      <>
        <ServiceAccountSelectInput
          type="ONEDRIVE"
          label={t(props.propertyKeys.serviceAccount)}
          value={props.integration.configuration.serviceAccount}
          fetchServiceAccounts={() => ServiceAccountService.getAll()}
          onChange={(value) => {
            props.updateConfiguration("serviceAccount", value);
          }}
          errorMessage={props.getError("serviceAccount")}
          required
          info={{
            title: t(props.propertyKeys.serviceAccount),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.serviceAccount,
            }),
            description: t(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.path)}
          value={props.integration.configuration.path}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("path", value);
          }}
          errorMessage={props.getError("path")}
          required
          info={{
            title: t(props.propertyKeys.path),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.path }),
            description: t(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_PATH_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.filename)}
          value={props.integration.configuration.filename}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("filename", value);
          }}
          errorMessage={props.getError("filename")}
          required
          info={{
            title: t(props.propertyKeys.filename),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
            description: t(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_FILENAME_DESCRIPTION),
          }}
        />

        <Checkbox
          block
          label={t(props.propertyKeys.csv)}
          checked={props.integration.configuration.csv}
          setChecked={(value) => {
            props.updateConfiguration("csv", value);
          }}
          info={{
            title: t(props.propertyKeys.csv),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.csv }),
            description: t(($) => $.ONEDRIVE_INTEGRATION_CONFIGURATION_CSV_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(OneDriveIntegration);
